import {cascadingGet, isNil, notNil, isEmpty} from '@megaphonenpm/mputils/src/utils.js';
import {ScreenComponent} from "./ScreenComponent.js";
import {play} from "../Animation.js";
import {awaitRender, div, fitText, setText} from '@megaphonenpm/mputils/src/DOM.js';
import {cloneInstance} from "../GWDService.js";

/**
 * A clone of the GWD theme's "#main #headlineContainer", for use in the body of
 * content scenes such as Tweet to TV, Bulletin, and Teaser. This dynamically generated
 * body headline is distinct from the manually created "#main #headlineContainer" instance, which
 * is used for non-scene states, such as WINNER and END.
 */
export class BodyHeadlineComponent extends ScreenComponent {
  constructor (themeID, visibleProducts) {
    super(themeID, ['headlineContainer'], {visibleProducts: visibleProducts});
  }

  validate () {
    super.validate();

    if (isNil(this.visibleProducts)) {
      this.valid = false;
    }
  }

  build () {
    super.build();

    // Pre-flight check
    if (!this.valid) {
      return;
    }

    // Start build
    this.view = div('bodyHeadlineContainer', 'bodyHeadlineContainer');
    // Main container layout
    this.view.classList.add('flex', 'flexExpandingItem');

    // Indicate the states and products during which the view should be visible
    this.view.classList.add('state_scene', 'state_scene_result');
    this.visibleProducts.forEach(productName => {
      this.view.classList.add(`product_${productName}`);
    });

    this.headline = cloneInstance(this.themeID, 'headlineContainer');
    this.headline.classList.add('flex', 'flexExpandingItem', 'flexCenterChildren');
    // Within the theme, inside Google Web Designer, the #headlineContainer <div> is assigned
    // the CSS class .headlineContainer. That .headlineContainer class applies when the
    // #headlineContainer <div> is used within the #main <div> only. In this TitleComponent class,
    // the #headlineContainer clone is automatically sized and positioned within
    // the .bodyContent <div>. The clone's author-time styles would conflict with the automatic
    // layout applied in this TitleComponent class, so the following code removes those styles.
    this.headline.classList.remove('headlineContainer');

    // Copy UGC layout visibility classes from GWD's "#main #headlineContainer" to this component's
    // main view class so the same intended UGC visibility applies to this BodyHeadlineComponent
    // instance.
    this.headline.classList.forEach(className => {
      if (className.startsWith('layout_ugc')) {
        this.view.classList.add(className);
      }
    });

    // Convenience reference to headline_txt
    this.headline_txt = this.headline.querySelector('.headline_txt');
    // Prevent headline_txt from growing taller than the container (this causes long text
    // to overflow out the bottom of the container rather than the top and bottom).
    this.headline_txt.classList.add('constrainHeight');

    this.view.append(this.headline);
  }

  setText (text) {
    if (!this.valid) {
      return;
    }

    this.headline_txt.classList.remove('empty');
    if (isEmpty(text)) {
      this.headline_txt.classList.add('empty');
    }

    this.headline_txt.style.removeProperty('font-size');
    setText([this.headline_txt], text);
    // Wait for flex layout to finish size adjustments, then automatically shrink headline_txt's
    // font if necessary.
    awaitRender().then(() => {
      fitText(this.headline_txt, this.headline_txt.parentNode.clientWidth, this.headline_txt.parentNode.clientHeight);
    });
  }

  /**
   * Plays the intro animation
   */
  playIntro () {
    // TODO: Implement option to define product-specific animations via Editor (e.g., one animation for
    //  Bulletin and a different animation for Tweet to TV. Will require passing the product ID to the
    //  current BodyHeadlineComponent instance.

    let productAnimation = cascadingGet(this.theme,[
      'design.products.title_products.screen.animation.main.start.label',
      'design.default.screen.animation.main.start.label']);
    let delay = cascadingGet(this.theme,
      ['design.products.title_products.screen.animation.main.start.delay',
        'design.default.screen.animation.main.start.delay']);
    if (notNil(productAnimation)) {
      play(this.view, productAnimation, delay);
    }

    // Play the theme's 'start' animation for the headline
    play(this.headline, 'start');
  }
}
